'use client';
import { HTMLAttributes, useCallback, useEffect, useRef, useState, type JSX } from 'react';
import styles from './styles.module.css';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { cn } from '@/lib/utils';
import { avertaRegular } from '@/styles/fonts';

export interface IActivableItem {
  activated: boolean;
}

interface ICarouselProps extends HTMLAttributes<HTMLDivElement> {
  items: JSX.Element[];
  isPageSelectable?: boolean;
  invisibleButtons?: boolean;
  itemStyle?: string;
  buttonsIconsStyle?: string;
  buttonsStyle?: string;
  containerStyle?: string;
  itemPadding?: number;
  leftButtonStyle?: string;
  rightButtonStyle?: string;
  pageSelectStyle?: string;
  showPreview?: boolean;
  hasBannerNavOptions?: boolean;
  bannerNavOptions?: string[];
}

export const Carousel: React.FC<ICarouselProps> = ({
  items = [],
  className,
  isPageSelectable = false,
  invisibleButtons = false,
  itemStyle = '',
  itemPadding = 8,
  buttonsIconsStyle = '',
  containerStyle = '',
  pageSelectStyle = '',
  buttonsStyle = '',
  leftButtonStyle = '',
  rightButtonStyle = '',
  showPreview = false,
  hasBannerNavOptions = false,
  bannerNavOptions,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isDragStart, setIsDragStart] = useState(false);
  const [prevPageX, setPrevPageX] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [isChangingPage, setIsChangingPage] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(0);
  const [itemsLoop, setItemsLoop] = useState([] as JSX.Element[]);

  const carouselRef = useRef<HTMLDivElement>(null);
  const carouselScrollRef = useRef<HTMLDivElement>(null);

  const changeItemList = (pagesToMove: number = 0, totalItems: number = 0) => {
    const lengthDifference =
      totalItems > 0 ? Math.abs(itemsLoop.length - totalItems) : 0;

    const absolutePagesToMove = Math.abs(pagesToMove);
    const itemsPerPage = getItemsPerPage();

    const firstElementInPageIndex = items.length - 1;
    const lastElementInPage = firstElementInPageIndex + itemsPerPage - 1;

    if (pagesToMove !== 0) {
      setItemsLoop((prevState) =>
        pagesToMove > 0
          ? [
            ...prevState.slice(absolutePagesToMove),
            ...prevState.slice(
              lastElementInPage,
              lastElementInPage + absolutePagesToMove,
            ),
          ]
          : [
            ...prevState.slice(
              firstElementInPageIndex - absolutePagesToMove + 1,
              firstElementInPageIndex + 1,
            ),
            ...prevState.slice(0, -absolutePagesToMove),
          ],
      );
    }
  };

  const changePage = (page: number) => {
    if (!carouselRef.current || !carouselScrollRef.current) {
      return;
    }

    const carousel = carouselRef.current;
    const carouselScroll = carouselScrollRef.current;

    const middlePage = items.length - 1;

    let changingToPage = page;

    const carouselTotalPages = items.length - 1;

    const pagesToMove = page - currentPage;
    const isMovingToRight = 0 < pagesToMove;

    if (pagesToMove === 0) {
      carouselScroll.scrollLeft = getCarouselMiddlePosition();
      carouselScrollRef.current?.classList.remove(styles['dragging']);
      return;
    }

    setIsChangingPage(true);

    if (page > carouselTotalPages) {
      changingToPage = 0;
    }

    if (page < 0) {
      changingToPage = carouselTotalPages;
    }

    setCurrentPage(changingToPage);

    const firstElementInPage = middlePage;
    const lastElementInPage = firstElementInPage + getItemsPerPage() - 1;
    const elementToCloneIndex = isMovingToRight
      ? lastElementInPage
      : firstElementInPage;

    const previousElement = carousel.childNodes[
      elementToCloneIndex - pagesToMove
    ] as HTMLDivElement;
    const currentElement = carousel.childNodes[
      elementToCloneIndex
    ] as HTMLDivElement;

    const elementWidth = currentElement.offsetWidth;

    const newPageDistance = elementWidth * pagesToMove * -1;

    changeItemList(pagesToMove);

    carousel.style.transition = `0s linear`;
    carousel.style.transform = `translateX(${-newPageDistance -
      (carouselScroll.scrollLeft - getCarouselMiddlePosition())
      }px)`;

    previousElement.classList.add('is-focused');
    currentElement.classList.remove('is-focused');
    setTimeout(() => {
      carousel.style.transition = `0.3s linear`;
      carousel.style.transform = `translateX(0px)`;
    }, 50);

    carouselScroll.scrollLeft = getCarouselMiddlePosition();

    setTimeout(() => {
      previousElement.classList.remove('is-focused');
      currentElement.classList.add('is-focused');

      carouselScrollRef.current?.classList.remove(styles['dragging']);

      setIsChangingPage(false);
    }, 300);
  };

  const dragStart = useCallback((e: MouseEvent | TouchEvent) => {
    setIsDragStart(true);

    e.stopPropagation();
    e.preventDefault();

    const pageX = e instanceof MouseEvent ? e.pageX : e.touches[0].pageX;

    setPrevPageX(pageX);
    carouselScrollRef.current?.classList.add(styles['dragging']);
  }, []);

  const autoSlide = (e: MouseEvent | TouchEvent) => {
    const pageX =
      e instanceof MouseEvent ? e.pageX : e.changedTouches[0]?.pageX || 0;
    const positionDifference = pageX - prevPageX;

    const isMoving = Math.abs(positionDifference) > getCarouselWidth() / 8;
    const isMovingToRight = positionDifference < 0;

    if (!isMoving) {
      return changePage(currentPage);
    }

    if (isMovingToRight) {
      return changePage(currentPage + 1);
    }

    return changePage(currentPage - 1);
  };

  const dragging = (e: MouseEvent | TouchEvent) => {
    if (!isDragStart || !carouselScrollRef.current) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();

    if (!isDragging) {
      setIsDragging(true);
    }

    const carouselScroll = carouselScrollRef.current;
    const pageX = e instanceof MouseEvent ? e.pageX : e.touches[0].pageX;

    const positionDifference =
      (pageX - prevPageX - itemPadding * 2) / itemsPerPage;

    carouselScroll.scrollLeft =
      getCarouselMiddlePosition() - positionDifference;
  };

  const dragStop = (e: MouseEvent | TouchEvent) => {
    setIsDragStart(false);

    e.stopPropagation();
    e.preventDefault();

    if (!isDragging) {
      return;
    }
    setIsDragging(false);
    autoSlide(e);
  };

  const prevItem = useCallback(() => {
    changePage(currentPage - 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const nextItem = useCallback(() => {
    changePage(currentPage + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const scrollCarrouselToMiddle = () => {
    if (carouselScrollRef.current) {
      const carouselScroll = carouselScrollRef.current;

      carouselScroll.scrollLeft = getCarouselMiddlePosition();
    }
  };

  const getCarouselWidth = () => {
    if (carouselRef.current) {
      const carousel = carouselRef.current;
      return carousel.clientWidth;
    }
    return 0;
  };

  const getItemWidth = () => {
    if (carouselRef.current) {
      const carousel = carouselRef.current;

      const itemElement = carousel.childNodes[0] as HTMLDivElement;
      return itemElement?.offsetWidth + itemPadding * 2 || 0;
    }
    return 0;
  };

  const getItemsPerPage = () => {
    const currentItemsPerPage = Math.round(getCarouselWidth() / getItemWidth());

    setItemsPerPage((prevState) => {
      if (prevState !== currentItemsPerPage) {
        return currentItemsPerPage;
      }
      return prevState;
    });

    return currentItemsPerPage;
  };

  const getCarouselMiddlePosition = () => {
    const middlePage = items.length - 1;

    return middlePage * (getItemWidth() - itemPadding * 2);
  };

  useEffect(() => {
    if (carouselScrollRef.current) {
      const carouselScroll = carouselScrollRef.current;

      carouselScroll.addEventListener('mousedown', dragStart);
      carouselScroll.addEventListener('touchstart', dragStart);
      carouselScroll.addEventListener('mousemove', dragging);
      carouselScroll.addEventListener('touchmove', dragging);
      carouselScroll.addEventListener('mouseup', dragStop);
      carouselScroll.addEventListener('touchend', dragStop);
      carouselScroll.addEventListener('mouseleave', dragStop);

      return () => {
        carouselScroll.removeEventListener('mousedown', dragStart);
        carouselScroll.removeEventListener('touchstart', dragStart);
        carouselScroll.removeEventListener('mousemove', dragging);
        carouselScroll.removeEventListener('touchmove', dragging);
        carouselScroll.removeEventListener('mouseup', dragStop);
        carouselScroll.removeEventListener('touchend', dragStop);
        carouselScroll.removeEventListener('mouseleave', dragStop);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselRef, dragStart, dragStop]);

  useEffect(() => {
    window.addEventListener('resize', scrollCarrouselToMiddle);
    window.addEventListener('resize', getItemsPerPage);

    return () => {
      window.removeEventListener('resize', scrollCarrouselToMiddle);
      window.removeEventListener('resize', getItemsPerPage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newItemsList = [
      ...items.slice(1),
      ...items,
      ...items.slice(0, itemsPerPage - items.length - 1),
    ];
    const lastElementInPage = items.length + itemsPerPage - 2;
    return setItemsLoop([
      ...newItemsList.slice(currentPage),
      ...newItemsList.slice(lastElementInPage, lastElementInPage + currentPage),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, itemsPerPage]);

  useEffect(() => {
    scrollCarrouselToMiddle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsLoop]);

  return (
    <div
      className={`relative items-center ${isPageSelectable && 'pb-24'
        } ${className}`}>
      <button
        disabled={isChangingPage}
        className={`
          rounded-full mb-16 box-border z-10 flex justify-center
          group items-center bg-white h-11 w-11 shadow-lg absolute top-1/2 left-0
          -translate-y-1/2 -translate-x-1/2 ${showPreview && 'desktop:left-56'}
          ${invisibleButtons && 'hidden'}
          ${buttonsStyle}
          ${leftButtonStyle}
          `}
        type="button"
        onClick={prevItem}>
        <FiChevronLeft
          className={`w-6 h-6 stroke-slate-500 opacity-30 group-hover:opacity-20 transition ${buttonsIconsStyle}`}
        />
      </button>
      <div
        ref={carouselScrollRef}
        className={`overflow-hidden box-border w-full h-full ${showPreview && 'desktop:px-[12vw]'
          }`}>
        <div
          ref={carouselRef}
          className={
            styles['carousel'] +
            ' whitespace-nowrap w-full mt-12 h-5/6 inline-block z-10 ' +
            containerStyle
          }>
          {(itemsLoop.length > 0 ? itemsLoop : items).map((item, index) => (
            <div
              key={index}
              className={`
              h-full text-white relative group px-[${itemPadding}px] align-top
              ${index === items.length - 1 && 'is-focused'}
              ${itemStyle ? itemStyle : 'w-full'}
            `}>
              {item}
            </div>
          ))}
        </div>
        <button
          disabled={isChangingPage}
          className={`
            rounded-full z-10 stroke-slate-500 bg-white h-11 flex justify-center
            group items-center w-11 shadow-lg absolute top-1/2 right-0
            -translate-y-1/2 translate-x-1/2  ${showPreview && 'desktop:right-56'
            }
            ${invisibleButtons && 'hidden'}
            ${buttonsStyle}
            ${rightButtonStyle} 
            `}
          type="button"
          onClick={nextItem}>
          <FiChevronRight
            className={`w-6 h-6 stroke-slate-500 opacity-30 group-hover:opacity-20 transition ${buttonsIconsStyle}`}
          />
        </button>
      </div>

      {isPageSelectable && (
        <div
          className={`flex flex-row space-x-2 w-full min-w-2 mt-6 justify-center ${pageSelectStyle}`}>
          {items.map((item, index) => (
            <button
              key={index}
              type="button"
              disabled={isChangingPage}
              className="p-2 cursor-pointer"
              onClick={() => changePage(index)}>
              <div
                className={`
                rounded-full min-w-[6px] w-[6px] h-[6px]
                ease-in-out duration-200 transition-colors
                outline-2 outline outline-offset-0
                ${currentPage === index
                    ? 'bg-transparent outline-primary'
                    : 'bg-zinc-200 outline-white hover:bg-primary'
                  }
              `}
              />
            </button>
          ))}
        </div>
      )}

      {hasBannerNavOptions && (
        <div
          className={`absolute hidden desktop:flex flex-row flex-wrap space-x-8 w-full bottom-[90px] justify-center px-[8px]`}>
          {bannerNavOptions &&
            bannerNavOptions.map((item, index) => (
              <button
                key={index}
                type="button"
                disabled={isChangingPage}
                onClick={() => changePage(index)}
                className={`${currentPage !== index && 'brightness-75'
                  } hover:brightness-100`}>
                <span
                  className={cn(
                    `${currentPage === index && 'underline'
                    } text-white text-xl`,
                    avertaRegular.className,
                  )}>
                  {item}
                </span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
};
